import React, { useState, useEffect } from 'react';
import { cursosAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Search, Edit, Trash2, GraduationCap, Users, Eye, 
  Loader2, Calendar, Clock, Award, Check, X
} from 'lucide-react';

const AdminCapacitaciones = () => {
  const [cursos, setCursos] = useState([]);
  const [inscripciones, setInscripciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('cursos');

  const [formData, setFormData] = useState({
    nombre: '',
    modalidad: 'presencial',
    fecha_inicio: '',
    fecha_limite_inscripcion: '',
    duracion: '',
    objetivos: '',
    aranceles: '',
    contenido: '',
    docente_titular_nombre: '',
    docente_titular_matricula: '',
    docente_suplente_nombre: '',
    docente_suplente_matricula: '',
    certificacion: '',
    requisitos: '',
    descripcion: '',
    precio_usd: 0,
    vacantes: '',
    estado: 'publicado'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cursosRes, inscripcionesRes] = await Promise.all([
        cursosAPI.getAdmin(),
        cursosAPI.getTodasInscripciones()
      ]);
      setCursos(cursosRes.data);
      setInscripciones(inscripcionesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      modalidad: 'presencial',
      fecha_inicio: '',
      fecha_limite_inscripcion: '',
      duracion: '',
      objetivos: '',
      aranceles: '',
      contenido: '',
      docente_titular_nombre: '',
      docente_titular_matricula: '',
      docente_suplente_nombre: '',
      docente_suplente_matricula: '',
      certificacion: '',
      requisitos: '',
      descripcion: '',
      precio_usd: 0,
      vacantes: '',
      estado: 'publicado'
    });
    setEditMode(false);
    setSelectedCurso(null);
  };

  const openEditForm = (curso) => {
    setFormData({
      nombre: curso.nombre || '',
      modalidad: curso.modalidad || 'presencial',
      fecha_inicio: curso.fecha_inicio?.split('T')[0] || '',
      fecha_limite_inscripcion: curso.fecha_limite_inscripcion?.split('T')[0] || '',
      duracion: curso.duracion || '',
      objetivos: curso.objetivos || '',
      aranceles: curso.aranceles || '',
      contenido: curso.contenido || '',
      docente_titular_nombre: curso.docentes?.titular?.nombre || '',
      docente_titular_matricula: curso.docentes?.titular?.matricula || '',
      docente_suplente_nombre: curso.docentes?.suplente?.nombre || '',
      docente_suplente_matricula: curso.docentes?.suplente?.matricula || '',
      certificacion: curso.certificacion || '',
      requisitos: curso.requisitos || '',
      descripcion: curso.descripcion || '',
      precio_usd: curso.precio_usd || 0,
      vacantes: curso.vacantes || '',
      estado: curso.estado || 'publicado'
    });
    setSelectedCurso(curso);
    setEditMode(true);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) {
      toast.error('El nombre del curso es requerido');
      return;
    }

    setSaving(true);
    try {
      const data = {
        nombre: formData.nombre,
        modalidad: formData.modalidad,
        duracion: formData.duracion,
        objetivos: formData.objetivos,
        aranceles: formData.aranceles,
        contenido: formData.contenido,
        certificacion: formData.certificacion,
        requisitos: formData.requisitos,
        descripcion: formData.descripcion,
        precio_usd: parseFloat(formData.precio_usd) || 0,
        vacantes: formData.vacantes ? parseInt(formData.vacantes) : null,
        estado: formData.estado,
        fechas: []
      };

      // Fechas opcionales
      if (formData.fecha_inicio) {
        data.fecha_inicio = new Date(formData.fecha_inicio).toISOString();
      }
      if (formData.fecha_limite_inscripcion) {
        data.fecha_limite_inscripcion = new Date(formData.fecha_limite_inscripcion).toISOString();
      }

      // Docentes
      if (formData.docente_titular_nombre) {
        data.docentes = {
          titular: {
            nombre: formData.docente_titular_nombre,
            matricula: formData.docente_titular_matricula || null
          }
        };
        if (formData.docente_suplente_nombre) {
          data.docentes.suplente = {
            nombre: formData.docente_suplente_nombre,
            matricula: formData.docente_suplente_matricula || null
          };
        }
      }

      if (editMode && selectedCurso) {
        await cursosAPI.update(selectedCurso.id, data);
        toast.success('Capacitación actualizada');
      } else {
        await cursosAPI.create(data);
        toast.success('Capacitación creada');
      }

      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail;
      if (Array.isArray(msg)) {
        toast.error(msg.map(e => e.msg).join(', '));
      } else {
        toast.error(msg || 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (curso) => {
    if (!confirm(`¿Eliminar la capacitación "${curso.nombre}"?`)) return;
    
    try {
      await cursosAPI.delete(curso.id);
      toast.success('Capacitación eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleUpdateEstadoInscripcion = async (inscripcion, nuevoEstado) => {
    try {
      await cursosAPI.updateEstadoInscripcion(inscripcion.id, nuevoEstado);
      toast.success(`Estado actualizado a: ${nuevoEstado}`);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Parsear solo la parte de fecha para evitar problemas de timezone
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-AR');
  };

  const filteredCursos = cursos.filter(c =>
    c.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'publicado': return <Badge className="bg-green-500">Publicado</Badge>;
      case 'borrador': return <Badge variant="outline">Borrador</Badge>;
      case 'finalizado': return <Badge className="bg-gray-500">Finalizado</Badge>;
      default: return <Badge variant="outline">{estado}</Badge>;
    }
  };

  const getEstadoInscripcionBadge = (estado) => {
    switch (estado) {
      case 'pendiente_pago': return <Badge className="bg-yellow-500">Pendiente Pago</Badge>;
      case 'pagado': return <Badge className="bg-blue-500">Pagado</Badge>;
      case 'confirmado': return <Badge className="bg-green-500">Confirmado</Badge>;
      case 'cancelado': return <Badge className="bg-red-500">Cancelado</Badge>;
      default: return <Badge variant="outline">{estado}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Gestión de Capacitaciones</h1>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="h-4 w-4 mr-2" /> Nueva Capacitación
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="cursos">
              <GraduationCap className="h-4 w-4 mr-2" /> Capacitaciones ({cursos.length})
            </TabsTrigger>
            <TabsTrigger value="inscripciones">
              <Users className="h-4 w-4 mr-2" /> Inscripciones ({inscripciones.length})
            </TabsTrigger>
          </TabsList>

          {/* Tab Capacitaciones */}
          <TabsContent value="cursos">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar capacitación..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredCursos.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No hay capacitaciones registradas
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCursos.map(curso => (
                      <div 
                        key={curso.id} 
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{curso.nombre}</h3>
                              {getEstadoBadge(curso.estado)}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Inicio: {formatDate(curso.fecha_inicio)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {curso.duracion || '-'}
                              </span>
                              <span>{curso.modalidad}</span>
                              {curso.precio_usd > 0 && (
                                <span className="font-medium">USD ${curso.precio_usd}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditForm(curso)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDelete(curso)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Inscripciones */}
          <TabsContent value="inscripciones">
            <Card>
              <CardContent className="pt-6">
                {inscripciones.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No hay inscripciones registradas
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Nombre</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">DNI</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Curso</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Fecha</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Estado</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {inscripciones.map(insc => (
                          <tr key={insc.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{insc.nombre_completo || insc.user_nombre}</td>
                            <td className="px-4 py-3 text-sm">{insc.email || insc.user_email}</td>
                            <td className="px-4 py-3 text-sm">{insc.dni || '-'}</td>
                            <td className="px-4 py-3 text-sm">{insc.curso_nombre}</td>
                            <td className="px-4 py-3 text-sm">{formatDate(insc.created_at)}</td>
                            <td className="px-4 py-3">{getEstadoInscripcionBadge(insc.estado)}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                {insc.estado !== 'confirmado' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600"
                                    onClick={() => handleUpdateEstadoInscripcion(insc, 'confirmado')}
                                    title="Confirmar"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                )}
                                {insc.estado !== 'cancelado' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600"
                                    onClick={() => handleUpdateEstadoInscripcion(insc, 'cancelado')}
                                    title="Cancelar"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modal Formulario */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Editar Capacitación' : 'Nueva Capacitación'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Información básica */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nombre del Curso *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Curso CRM - MRM - TEM"
                    required
                  />
                </div>

                <div>
                  <Label>Modalidad</Label>
                  <Select 
                    value={formData.modalidad} 
                    onValueChange={(v) => setFormData({ ...formData, modalidad: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="presencial">Presencial</SelectItem>
                      <SelectItem value="virtual">Virtual</SelectItem>
                      <SelectItem value="hibrido">Híbrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Estado</Label>
                  <Select 
                    value={formData.estado} 
                    onValueChange={(v) => setFormData({ ...formData, estado: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="borrador">Borrador</SelectItem>
                      <SelectItem value="publicado">Publicado</SelectItem>
                      <SelectItem value="finalizado">Finalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Fecha de Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Fecha Límite de Inscripción</Label>
                  <Input
                    type="date"
                    value={formData.fecha_limite_inscripcion}
                    onChange={(e) => setFormData({ ...formData, fecha_limite_inscripcion: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Duración</Label>
                  <Input
                    value={formData.duracion}
                    onChange={(e) => setFormData({ ...formData, duracion: e.target.value })}
                    placeholder="Ej: 16 horas, 2 días"
                  />
                </div>

                <div>
                  <Label>Precio (USD $)</Label>
                  <Input
                    type="number"
                    value={formData.precio_usd}
                    onChange={(e) => setFormData({ ...formData, precio_usd: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="1"
                  />
                </div>

                <div>
                  <Label>Vacantes (límite de alumnos)</Label>
                  <Input
                    type="number"
                    value={formData.vacantes}
                    onChange={(e) => setFormData({ ...formData, vacantes: e.target.value })}
                    placeholder="Ej: 25"
                    min="1"
                  />
                </div>
              </div>

              {/* Aranceles */}
              <div>
                <Label>Aranceles (descripción)</Label>
                <Input
                  value={formData.aranceles}
                  onChange={(e) => setFormData({ ...formData, aranceles: e.target.value })}
                  placeholder="Ej: Incluye material y certificado. Descuento 10% pago anticipado"
                />
              </div>

              {/* Objetivos */}
              <div>
                <Label>Objetivos</Label>
                <Textarea
                  value={formData.objetivos}
                  onChange={(e) => setFormData({ ...formData, objetivos: e.target.value })}
                  placeholder="Objetivos del curso..."
                  rows={3}
                />
              </div>

              {/* Contenido */}
              <div>
                <Label>Contenido / Temario</Label>
                <Textarea
                  value={formData.contenido}
                  onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                  placeholder="Temario del curso..."
                  rows={4}
                />
              </div>

              {/* Docentes */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium mb-3">Docentes</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Titular - Nombre</Label>
                    <Input
                      value={formData.docente_titular_nombre}
                      onChange={(e) => setFormData({ ...formData, docente_titular_nombre: e.target.value })}
                      placeholder="Nombre del docente titular"
                    />
                  </div>
                  <div>
                    <Label>Titular - Matrícula</Label>
                    <Input
                      value={formData.docente_titular_matricula}
                      onChange={(e) => setFormData({ ...formData, docente_titular_matricula: e.target.value })}
                      placeholder="N° de matrícula"
                    />
                  </div>
                  <div>
                    <Label>Suplente - Nombre</Label>
                    <Input
                      value={formData.docente_suplente_nombre}
                      onChange={(e) => setFormData({ ...formData, docente_suplente_nombre: e.target.value })}
                      placeholder="Nombre del docente suplente"
                    />
                  </div>
                  <div>
                    <Label>Suplente - Matrícula</Label>
                    <Input
                      value={formData.docente_suplente_matricula}
                      onChange={(e) => setFormData({ ...formData, docente_suplente_matricula: e.target.value })}
                      placeholder="N° de matrícula"
                    />
                  </div>
                </div>
              </div>

              {/* Certificación */}
              <div>
                <Label>Certificación</Label>
                <Input
                  value={formData.certificacion}
                  onChange={(e) => setFormData({ ...formData, certificacion: e.target.value })}
                  placeholder="Ej: Certificado avalado por ANAC"
                />
              </div>

              {/* Requisitos */}
              <div>
                <Label>Requisitos</Label>
                <Textarea
                  value={formData.requisitos}
                  onChange={(e) => setFormData({ ...formData, requisitos: e.target.value })}
                  placeholder="Requisitos para inscribirse..."
                  rows={2}
                />
              </div>

              {/* Descripción */}
              <div>
                <Label>Descripción General</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Descripción breve del curso..."
                  rows={2}
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600" disabled={saving}>
                  {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : 
                   editMode ? 'Actualizar' : 'Crear Capacitación'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminCapacitaciones;
